import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { DottedMap } from "../components/ui/dotted-map";

interface AppErrorFallbackProps {
    /** Resets the Sentry ErrorBoundary so the app can try to re-render. */
    onReset: () => void;
}

/**
 * Global crash screen rendered by the root Sentry ErrorBoundary.
 * Replaces the previous `fallback={null}` (blank white screen) with a
 * recoverable UI: the user can retry the render or do a full reload.
 *
 * Shares the sign-in screen's backdrop (dotted world map over a slate base)
 * so an unexpected crash still lands on brand-consistent surface.
 */
export function AppErrorFallback({ onReset }: AppErrorFallbackProps) {
    const { t } = useTranslation();

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-100 p-6 dark:bg-background">
            {/* Dotted Map Full Background — same backdrop as the sign-in screen. */}
            <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center opacity-80">
                <DottedMap
                    className="w-[200%] md:w-[150%] lg:w-[120%] max-w-[1800px]"
                    dotColor="#c4b5fd"
                    markerColor="#7c3aed"
                    markers={[
                        { lat: 40.7128, lng: -74.006 }, // New York
                        { lat: 37.5665, lng: 126.978 }, // Seoul
                        { lat: -23.5505, lng: -46.6333 }, // São Paulo
                    ]}
                />
            </div>

            {/* Crash Card */}
            <div className="relative z-10 w-full max-w-md px-4">
                <div className="flex flex-col items-center gap-6 rounded-2xl border border-border bg-card p-8 text-center shadow-xl">
                    <AlertTriangle
                        className="h-12 w-12 text-destructive"
                        aria-hidden="true"
                    />
                    <div className="space-y-2">
                        <h1 className="text-xl font-semibold text-foreground">
                            {t("common:errorBoundary.title", "Algo deu errado")}
                        </h1>
                        <p className="max-w-md text-sm text-muted-foreground">
                            {t(
                                "common:errorBoundary.description",
                                "Ocorreu um erro inesperado e a tela não pôde ser exibida. Já fomos notificados — tente novamente.",
                            )}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Button variant="outline" onClick={onReset}>
                            {t(
                                "common:errorBoundary.retry",
                                "Tentar novamente",
                            )}
                        </Button>
                        <Button onClick={() => window.location.reload()}>
                            {t(
                                "common:errorBoundary.reload",
                                "Recarregar página",
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
